import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import emendaService, { resolveEmendaStatus, deriveSimplifiedStatus, type EmendaAnexoDTO, type EmendaHistoricoDTO } from '../services/emendaService';
import EmendaVinculoOrcamentarioPanel from '../components/EmendaVinculoOrcamentarioPanel';
import tipoEmendaService, { resolveTipoEmendaNome, type TipoEmendaDTO } from '../services/tipoEmendaService';
import funcaoOrcamentariaService, { type FuncaoOrcamentariaDTO } from '../services/funcaoOrcamentariaService';
import AdmissibilidadePanel from '../components/AnalistaOrcamentoPanel';
import DotacaoOrcamentariaCombobox, { type DotacaoOrcamentaria } from '../components/DotacaoOrcamentariaCombobox';
import SecretariaDemandaPanel from '../components/SecretariaDemandaPanel';
import ConveniosDocumentalPanel from '../components/ConveniosDocumentalPanel';
import OrcamentoDevolucaoPanel from '../components/OrcamentoDevolucaoPanel';
import planoTrabalhoService from '../services/planoTrabalhoService';
import type { PlanoTrabalho } from '../types/planoTrabalho.types';
import EmendaHistoricoTimeline from '../components/EmendaHistoricoTimeline';
import { useAuth } from '../context/AuthContext';
import NovaSolicitacaoButton from '../components/NovaSolicitacaoButton';
import useEmendaAttachments from '../hooks/useEmendaAttachments';
import { UserRole, Permission } from '../types/user.types';
import { emendaStatusTone, fMoney, fDate } from '../utils/emendaFormatters';
import AuthenticatedFileButton from '../components/AuthenticatedFileButton';
import { FIELD_CLASS } from '../components/ui/fieldStyles';
import Button from '../components/ui/Button';
import { toUpperText } from '../utils/formatters';

type Emenda = {
  id: string;
  officialCode?: string;
  exercicio?: number;
  councilorId?: string;
  councilorName?: string;
  date?: string;
  value?: number | string;
  classification?: string;
  classificacao?: string | number;
  classificacaoLabel?: string;
  tipoTransferencia?: string;
  esfera?: string;
  funcaoCodigo?: string;
  status?: string;
  statusCicloVida?: string;
  institutionName?: string;
  institutionEmail?: string;
  existeConvenio?: boolean;
  numeroConvenio?: string;
  anoConvenio?: number;
  signedLink?: string;
  description?: string;
  objectDetail?: string;
  justificativa?: string;
  previsaoConclusao?: string;
  secretariaDestino?: string;
  dotacaoOrcamentariaId?: number;
  dotacaoOrcamentariaTexto?: string;
  dotacoes?: { dotacaoOrcamentariaId?: number; dotacaoOrcamentariaTexto?: string }[];
  atoNormativoTipo?: string;
  atoNormativoNumero?: string;
  atoNormativoAno?: number;
  atoNormativoUrl?: string;
  leiOrcamentariaId?: string;
};

const WORKFLOW_STEPS = [
  { label: 'Recebido', owner: 'Câmara' },
  { label: 'Admissibilidade', owner: 'Orçamento' },
  { label: 'Demanda', owner: 'Secretaria' },
  { label: 'Documental', owner: 'Convênios' },
  { label: 'Em execução', owner: 'Plano de trabalho' },
  { label: 'Concluído', owner: '—' },
];

// A etapa Jurídica acontece dentro do plano de trabalho, não na tramitação da
// emenda — por isso os status pós-documental e de execução caem todos no passo
// Execução (4), ligado ao plano de trabalho.
const STATUS_TO_STEP: Record<string, number> = {
  'Em aprovação': 0,
  'Recebido': 0,
  'Em análise de admissibilidade': 1,
  // Admissibilidade aprovada → handoff para a Secretaria (Demanda).
  'Admissibilidade aprovada': 2,
  'Devolvida ao legislativo': 1,
  'Em análise de demanda': 2,
  // Demanda aprovada → handoff para Documental (Indireta) / execução direta.
  'Análise de demanda aprovada': 3,
  'Retornada ao Orçamento (demanda)': 2,
  'Devolvida por incompatibilidade de demanda': 2,
  'Em análise documental': 3,
  'Retornada ao Orçamento (documental)': 3,
  'Devolvida por inviabilidade documental': 3,
  'Análise documental aprovada': 4,
  'Iniciado': 4,
  'Em execução': 4,
  'Devolvido': 4,
  'Concluído': 5,
};

const STATUS_OPTIONS = [
  'Em aprovação', 'Recebido', 'Em análise de admissibilidade', 'Admissibilidade aprovada',
  'Devolvida ao legislativo', 'Em análise de demanda', 'Análise de demanda aprovada',
  'Retornada ao Orçamento (demanda)', 'Devolvida por incompatibilidade de demanda',
  'Em análise documental', 'Análise documental aprovada',
  'Retornada ao Orçamento (documental)', 'Devolvida por inviabilidade documental',
  'Iniciado', 'Em execução', 'Concluído', 'Devolvido',
];

const PLANO_STATUS_LABEL: Record<string, string> = {
  RASCUNHO: 'Rascunho',
  AGUARDANDO_ASSINATURA: 'Aguardando assinatura',
  EM_ANALISE: 'Em análise',
  APROVADO: 'Aprovado',
  DEVOLVIDO_CORRECAO: 'Devolvido para correção',
  REPROVADO: 'Reprovado',
  CONCLUIDO: 'Concluído',
  FECHADO: 'Fechado',
};

const PLANO_STATUS_TONE: Record<string, string> = {
  APROVADO: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  CONCLUIDO: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  FECHADO: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300',
  REPROVADO: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300',
  DEVOLVIDO_CORRECAO: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/30 dark:text-red-300',
  EM_ANALISE: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  AGUARDANDO_ASSINATURA: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

// ── Sub-components ────────────────────────────────────────────────────────────

const ChevronDown: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className={`h-4 w-4 ${className}`}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const BackArrow: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
    <path d="M15 18l-6-6 6-6" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" className="h-3 w-3">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);

// ── Main component ────────────────────────────────────────────────────────────

const EmendaDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { hasRole, user, hasPermission } = useAuth();
  const isAdmin = hasRole(UserRole.ADMIN);
  const isOrcamento = hasRole(UserRole.ORCAMENTO);
  const isCamara = hasRole(UserRole.CAMARA);
  const isSecretaria = hasRole(UserRole.SECRETARIA);
  const isConvenios = hasRole(UserRole.CONVENIOS);
  const canEdit = hasPermission(Permission.EDIT_EMENDA);
  const canEditVinculoOrcamentario = hasPermission(Permission.EDIT_EMENDA_VINCULO_ORCAMENTARIO);
  const canManageLeis = hasPermission(Permission.MANAGE_LEIS_ORCAMENTARIAS);
  const canViewExecucaoFinanceira = hasPermission(Permission.VIEW_EXECUCAO_FINANCEIRA);
  // Espelha EmendasResource.podeExecutarAcao no backend: as ações de admissibilidade
  // (INICIAR_ANALISE/APROVAR_ADMISSIBILIDADE/REPROVAR_ADMISSIBILIDADE/DEVOLVER_CAMARA)
  // são fixas por papel — ORCAMENTO é dono da etapa por design, sem checagem de
  // permissão (mesmo padrão de SECRETARIA na demanda e CONVENIOS no documental).
  const canActAdmissibilidade = isAdmin || isOrcamento;

  const [emenda, setEmenda] = useState<Emenda | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [historico, setHistorico] = useState<EmendaHistoricoDTO[]>([]);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [tiposEmenda, setTiposEmenda] = useState<TipoEmendaDTO[]>([]);
  const [funcoesOrcamentarias, setFuncoesOrcamentarias] = useState<FuncaoOrcamentariaDTO[]>([]);
  const [showAlterarStatusModal, setShowAlterarStatusModal] = useState(false);
  const [novoStatusSelecionado, setNovoStatusSelecionado] = useState('');
  const [justificativaStatus, setJustificativaStatus] = useState('');
  const [alterandoStatus, setAlterandoStatus] = useState(false);
  const [alterarStatusErro, setAlterarStatusErro] = useState<string | null>(null);
  const [showHistoricoModal, setShowHistoricoModal] = useState(false);
  const [aprovandoCamara, setAprovandoCamara] = useState(false);
  const [aprovarCamaraErro, setAprovarCamaraErro] = useState<string | null>(null);
  const [tipoCamara, setTipoCamara] = useState('');
  const [esferaCamara, setEsferaCamara] = useState('');
  const [dotacaoCamara, setDotacaoCamara] = useState<{ id?: number; texto: string }>({ texto: '' });
  const [dotacoesOpcoes, setDotacoesOpcoes] = useState<DotacaoOrcamentaria[]>([]);
  const [showRecebidoCamaraForm, setShowRecebidoCamaraForm] = useState(false);
  const [recebidoForm, setRecebidoForm] = useState({ leiNumero: '', leiDescricao: '', dataRecebimento: '' });
  const [savingRecebido, setSavingRecebido] = useState(false);
  const [recebidoErro, setRecebidoErro] = useState<string | null>(null);
  const [plano, setPlano] = useState<PlanoTrabalho | null>(null);
  const [publicarEvidencia, setPublicarEvidencia] = useState(false);
  const [evidenciaForm, setEvidenciaForm] = useState({ categoriaTaep: '', descricaoPublica: '', dataDocumento: '', redacaoConfirmada: false });
  const [evidenciaErro, setEvidenciaErro] = useState<string | null>(null);
  const { anexos, historico: hookHistorico, uploading, deletingId, inputRef, uploadFile, deleteAnexo } = useEmendaAttachments(id, user);

  useEffect(() => {
    if (!id) return;
    planoTrabalhoService.getByEmenda(id).then(setPlano).catch(() => setPlano(null));
  }, [id]);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setLoadingHistorico(true);
    api.get<Emenda>(`/emendas/${id}/with-details`).then(({ data }) => {
      setEmenda(data);
      return Promise.all([
        emendaService.getHistorico(id).catch(() => [] as EmendaHistoricoDTO[]),
        tipoEmendaService.list().catch(() => [] as TipoEmendaDTO[]),
        funcaoOrcamentariaService.list().catch(() => [] as FuncaoOrcamentariaDTO[]),
      ]);
    }).then(([hist, tipos, funcoes]) => {
      setHistorico(hist);
      setTiposEmenda(Array.from(new Map((tipos || []).map((t) => [t.codigo, t])).values()));
      setFuncoesOrcamentarias(funcoes);
    }).catch(() => setError('Falha ao carregar a emenda')).finally(() => {
      setLoading(false);
      setLoadingHistorico(false);
    });
  }, [id]);

  useEffect(() => setHistorico(hookHistorico), [hookHistorico]);

  // Close the history drawer on Escape and move focus into it when it opens.
  const historicoDrawerRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!showHistoricoModal) return;
    historicoDrawerRef.current?.focus();
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setShowHistoricoModal(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showHistoricoModal]);

  const statusWorkflow = resolveEmendaStatus(emenda);
  // Direta pula a etapa Documental: demanda aprovada vai direto para Execução.
  const isDireta = emenda?.tipoTransferencia !== 'Indireta';
  // "Devolvido" é um status genérico (ação de despacho) que não indica de qual
  // etapa a emenda veio — todas as devoluções caem nele. Para não exibir sempre
  // "Em execução", busca no histórico o status anterior à última devolução
  // genérica e usa a etapa correspondente a ele.
  const activeStep = (() => {
    if (statusWorkflow === 'Análise de demanda aprovada' && isDireta) return 4;
    if (statusWorkflow === 'Devolvido') {
      const statusOrigem = [...historico]
        .sort((a, b) => new Date(b.dataHora).getTime() - new Date(a.dataHora).getTime())
        .find((h) => h.statusNovo === 'Devolvido')?.statusAnterior;
      if (statusOrigem && statusOrigem in STATUS_TO_STEP) return STATUS_TO_STEP[statusOrigem];
    }
    return STATUS_TO_STEP[statusWorkflow] ?? 0;
  })();
  // Estados de devolução (Devolvida.../Devolvido) recebem destaque vermelho.
  const isReturned = /^devolvid/i.test(statusWorkflow ?? '');
  // Retornada ao Orçamento: reprovada na análise, aguardando o Orçamento
  // formalizar a devolução à Câmara (ação DEVOLVER_CAMARA).
  const isRetornoOrcamento = /^retornada ao or/i.test(statusWorkflow ?? '');
  // True when the current user owns a dedicated action panel for the active stage.
  // Used to hide the generic "Despacho" panel so only one action area shows.
  // A secretaria vê emendas de toda a sua secretaria (visão geral), mas só pode
  // agir naquelas de fato destinadas a ela. Quando o usuário não tem secretaria
  // definida ou a emenda não tem destino, mantém o comportamento anterior.
  const userSecretaria = (user as any)?.secretaria as string | undefined;
  const isEmendaDaMinhaSecretaria =
    !userSecretaria ||
    !emenda?.secretariaDestino ||
    emenda.secretariaDestino.toLowerCase() === userSecretaria.toLowerCase();
  const canActSecretaria = isAdmin || (isSecretaria && isEmendaDaMinhaSecretaria);
  const canActOnStep = (() => {
    switch (activeStep) {
      case 0:
      case 1: return canActAdmissibilidade;
      case 2: return canActSecretaria;
      case 3: return (isAdmin || isConvenios) && emenda?.tipoTransferencia === 'Indireta';
      // Execução (4) é dirigida pelo plano de trabalho — sem ação do usuário aqui.
      default: return false;
    }
  })();
  // "Recebido da Câmara" oculto no front por enquanto (lógica/backend mantidos).
  const RECEBIDO_CAMARA_ENABLED: boolean = false;

  const generalAnexos = useMemo(() => (anexos || []).filter((a) => !a?.isDespacho), [anexos]);
  const itemsHistorico = useMemo(() => [...historico].sort((a, b) => new Date(b.dataHora).getTime() - new Date(a.dataHora).getTime()), [historico]);

  const funcao = emenda?.funcaoCodigo
    ? funcoesOrcamentarias.find((f) => f.codigo === emenda.funcaoCodigo)?.descricao || emenda.funcaoCodigo
    : null;
  const classificacao = emenda?.classificacaoLabel
    || (String(emenda?.classificacao) === '1' ? 'Custeio' : String(emenda?.classificacao) === '2' ? 'Investimento' : null);

  const refreshWorkflowState = (newDetailedStatus: string) => {
    if (!id) return;
    setEmenda((prev) => prev ? { ...prev, statusCicloVida: newDetailedStatus, status: deriveSimplifiedStatus(newDetailedStatus) } : prev);
    emendaService.getHistorico(id).then(setHistorico).catch(() => {});
  };

  const handleAlterarStatus = async () => {
    if (!id || !novoStatusSelecionado) return;
    setAlterandoStatus(true);
    setAlterarStatusErro(null);
    try {
      const updated = await emendaService.alterarStatus(id, novoStatusSelecionado, justificativaStatus || undefined);
      setEmenda((prev) => prev ? { ...prev, statusCicloVida: updated.statusCicloVida, status: updated.status || deriveSimplifiedStatus(updated.statusCicloVida) } : prev);
      setHistorico(await emendaService.getHistorico(id));
      setShowAlterarStatusModal(false);
      setNovoStatusSelecionado('');
      setJustificativaStatus('');
    } catch (err: any) {
      setAlterarStatusErro(err?.response?.data?.error || err?.message || 'Erro ao alterar status');
    } finally {
      setAlterandoStatus(false);
    }
  };

  // Emenda 2027+ nasce "Em aprovação" na Câmara; a aprovação a envia à Prefeitura (Recebido).
  const emAprovacaoCamara = statusWorkflow === 'Em aprovação';
  const canAprovarCamara = emAprovacaoCamara && (isCamara || isAdmin);

  // Dotações do exercício da emenda (na área dela, quando houver) para a Câmara escolher no envio.
  const precisaDotacaoCamara = canAprovarCamara && !(emenda?.dotacaoOrcamentariaId || emenda?.dotacaoOrcamentariaTexto || (emenda?.dotacoes && emenda.dotacoes.length > 0));
  useEffect(() => {
    if (!precisaDotacaoCamara || !emenda?.exercicio) return;
    let ignore = false;
    const params = new URLSearchParams({ limit: '500', exercicio: String(emenda.exercicio) });
    if (emenda.funcaoCodigo) params.set('funcaoCodigo', emenda.funcaoCodigo);
    api.get<DotacaoOrcamentaria[]>(`/dotacoes-orcamentarias/search?${params.toString()}`)
      .then((r) => { if (!ignore) setDotacoesOpcoes(r.data); })
      .catch(() => { if (!ignore) setDotacoesOpcoes([]); });
    return () => { ignore = true; };
  }, [precisaDotacaoCamara, emenda?.exercicio, emenda?.funcaoCodigo]);

  const handleAprovarCamara = async () => {
    if (!id) return;
    const tipoFinal = emenda?.classification || tipoCamara;
    const esferaFinal = emenda?.esfera || esferaCamara;
    const temDotacao = !!(emenda?.dotacaoOrcamentariaId || emenda?.dotacaoOrcamentariaTexto || (emenda?.dotacoes && emenda.dotacoes.length > 0));
    const dotacaoInformada = dotacaoCamara.id !== undefined || dotacaoCamara.texto.trim() !== '';
    const faltando = [
      !tipoFinal && 'tipo da emenda',
      !esferaFinal && 'esfera',
      !temDotacao && !dotacaoInformada && 'dotação orçamentária',
    ].filter(Boolean);
    if (faltando.length > 0) {
      setAprovarCamaraErro(`Para enviar à Prefeitura, informe: ${faltando.join(', ')}.`);
      return;
    }
    setAprovandoCamara(true);
    setAprovarCamaraErro(null);
    try {
      const updated = await emendaService.executarAcao(id, {
        acao: 'APROVAR_CAMARA',
        tipoEmenda: tipoFinal,
        esfera: esferaFinal,
        dotacoes: !temDotacao && dotacaoInformada
          ? [{ dotacaoOrcamentariaId: dotacaoCamara.id, dotacaoOrcamentariaTexto: dotacaoCamara.texto }]
          : undefined,
      });
      refreshWorkflowState(updated.statusCicloVida || 'Recebido');
    } catch (err: any) {
      setAprovarCamaraErro(err?.response?.data?.error || err?.message || 'Erro ao aprovar a emenda');
    } finally {
      setAprovandoCamara(false);
    }
  };

  const handleRecebidoCamara = async () => {
    if (!id) return;
    if (!recebidoForm.leiNumero.trim() || !recebidoForm.leiDescricao.trim() || !recebidoForm.dataRecebimento) {
      setRecebidoErro('Preencha todos os campos obrigatórios');
      return;
    }
    setSavingRecebido(true);
    setRecebidoErro(null);
    try {
      await emendaService.registrarRecebidoCamara(id, {
        leiNumero: recebidoForm.leiNumero.trim(),
        leiDescricao: recebidoForm.leiDescricao.trim(),
        dataRecebimento: recebidoForm.dataRecebimento,
      });
      setHistorico(await emendaService.getHistorico(id));
      setShowRecebidoCamaraForm(false);
      setRecebidoForm({ leiNumero: '', leiDescricao: '', dataRecebimento: '' });
    } catch (err: any) {
      setRecebidoErro(err?.response?.data?.error || err?.message || 'Erro ao registrar');
    } finally {
      setSavingRecebido(false);
    }
  };

  const AnexoItem = ({ anexo }: { anexo: EmendaAnexoDTO }) => (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 p-3 dark:border-slate-700 dark:bg-slate-800/50">
      <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-sky-50 text-[10px] font-bold text-sky-700 dark:bg-sky-900/30 dark:text-sky-400">
        {anexo.mimeType?.includes('pdf') ? 'PDF' : 'ARQ'}
      </span>
      <div className="min-w-0 flex-1">
        <AuthenticatedFileButton
          url={`${api.defaults.baseURL ?? ''}${anexo.viewUrl}`}
          fileName={anexo.nomeOriginal}
          mimeType={anexo.mimeType}
          className="block max-w-full truncate text-left text-sm font-medium text-sky-700 hover:underline dark:text-sky-400"
        >
          {anexo.nomeOriginal}
        </AuthenticatedFileButton>
        <p className="mt-0.5 text-xs text-fg-subtle">
          {(anexo.tamanhoBytes / 1024).toFixed(0)} KB{anexo.dataUpload ? ` · enviado em ${new Date(anexo.dataUpload).toLocaleDateString('pt-BR')}` : ''}
        </p>
        {anexo.publicavel && <p className="mt-1 text-xs font-semibold text-emerald-700 dark:text-emerald-400">Evidência pública · {anexo.categoriaTaep?.replaceAll('_', ' ')}</p>}
      </div>
      <button
        type="button"
        disabled={deletingId === anexo.id}
        onClick={async () => { if (!window.confirm(`Remover "${anexo.nomeOriginal}"?`)) return; await deleteAnexo(anexo.id); }}
        className="rounded-lg p-1.5 text-red-500 hover:bg-red-50 disabled:opacity-40 dark:hover:bg-red-900/20"
      >
        ×
      </button>
    </div>
  );

  // ── Loading / error states ──────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-slate-500 dark:text-slate-400">Carregando detalhes da emenda...</p>
      </div>
    );
  }

  if (error || !emenda) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-danger-text">{error ?? 'Emenda não encontrada'}</p>
        <button type="button" onClick={() => navigate('/dashboard/emendas')} className="text-sm text-info-fg underline">
          Voltar para lista
        </button>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="w-full space-y-4">

      {/* Back link */}
      <button
        type="button"
        onClick={() => navigate('/dashboard/emendas')}
        className="inline-flex items-center gap-1.5 py-1 text-sm font-medium text-fg-muted transition hover:text-fg"
      >
        <BackArrow />
        Voltar para emendas
      </button>

      {/* ── Identity header ──────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          {/* Instituição */}
          <p className="mt-4 text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-400">
            {emenda.institutionName || 'Instituição'}
          </p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight text-slate-900 tabular-nums dark:text-slate-100">
            {emenda.officialCode || 'Detalhes da Emenda'}
          </h1>
          {/* Status atual em destaque (cor semântica: devolvida = vermelho, etc.) */}
          <span className={`mt-2 inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${emendaStatusTone(statusWorkflow)}`}>
            <span className="h-1.5 w-1.5 rounded-full bg-current" />
            {statusWorkflow}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {id && canViewExecucaoFinanceira && (
            <Button variant="secondary" onClick={() => navigate(`/dashboard/execucao-financeira/${id}`)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                <path d="M3 3v18h18" />
                <path d="m7 16 4-5 3 3 5-7" />
              </svg>
              Execução financeira
            </Button>
          )}
          <NovaSolicitacaoButton tituloTela="Detalhes da Emenda" />
          <Button variant="secondary" onClick={() => setShowHistoricoModal(true)}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
              <path d="M12 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10" />
              <polyline points="12 7 12 12 15 14" />
            </svg>
            Histórico
          </Button>
          {/* Edição só é permitida antes de iniciar a tramitação (status Recebido). */}
          {canEdit && activeStep === 0 && (
            <Button variant="secondary" onClick={() => navigate(`/dashboard/cadastro-emenda?id=${id}`)}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
              </svg>
              Editar
            </Button>
          )}
          {isAdmin && (
            <Button variant="secondary" onClick={() => { setNovoStatusSelecionado(statusWorkflow); setJustificativaStatus(''); setAlterarStatusErro(null); setShowAlterarStatusModal(true); }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                <path d="M21 2v6h-6" /><path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                <path d="M3 22v-6h6" /><path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
              </svg>
              Alterar status
            </Button>
          )}
        </div>
      </div>

      {/* Retornada ao Orçamento: aviso claro de que aguarda a devolução formal à Câmara. */}
      {isRetornoOrcamento && (
        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-800 dark:bg-amber-900/30">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
            <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
          </svg>
          <div className="text-sm text-amber-800 dark:text-amber-200">
            <p className="font-semibold">Aguardando devolução à Câmara</p>
            <p className="mt-0.5 text-xs leading-5">
              Emenda reprovada na análise e retornada ao setor de <b>Orçamento</b>, que precisa formalizar a devolução à Câmara para disponibilizá-la ao parlamentar.{' '}
              {canActAdmissibilidade ? 'Use o painel “Devolução à Câmara” abaixo.' : 'Essa ação é feita pelo setor de Orçamento.'}
            </p>
          </div>
        </div>
      )}

      {/* ── Stepper ──────────────────────────────────────────────────────────── */}
      <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
        <div className="mb-4 flex items-baseline justify-between gap-4">
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">Tramitação</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            Etapa {activeStep + 1} de {WORKFLOW_STEPS.length} ·{' '}
            {isReturned ? (
              <b className="font-semibold text-red-600 dark:text-red-400">devolvida em {WORKFLOW_STEPS[activeStep]?.label}</b>
            ) : isRetornoOrcamento ? (
              <b className="font-semibold text-amber-700 dark:text-amber-400">retornada ao Orçamento — aguardando devolução à Câmara</b>
            ) : (
              <>em <b className="font-semibold text-amber-700 dark:text-amber-400">{WORKFLOW_STEPS[activeStep]?.label}</b></>
            )}
          </span>
        </div>
        <ol className="grid grid-cols-6">
          {WORKFLOW_STEPS.map((step, i) => {
            // Emenda Direta: a etapa Documental (Convênios) não é exigida — exibida como "não se aplica".
            const isDocumentalPulada = isDireta && step.label === 'Documental';
            const isDone = i < activeStep && !isDocumentalPulada;
            const isActive = i === activeStep;
            return (
              <li
                key={step.label}
                className="relative flex flex-col items-center text-center"
                title={isDocumentalPulada ? 'Etapa não exigida na Emenda Direta — a Direta não passa pela análise documental (Convênios).' : undefined}
              >
                {/* Connector */}
                {i > 0 && (
                  <div
                    className={`absolute top-[11px] h-0.5 ${isDone ? 'bg-sky-400' : isActive ? (isReturned ? 'bg-gradient-to-r from-sky-500 to-red-400' : 'bg-gradient-to-r from-sky-500 to-amber-400') : 'bg-slate-200 dark:bg-slate-700'}`}
                    style={{ left: '-50%', width: '100%' }}
                  />
                )}
                {/* Dot */}
                <span
                  className={[
                    'relative z-10 flex items-center justify-center rounded-full border-2 font-bold transition-all duration-200',
                    isDocumentalPulada ? 'h-6 w-6 border-dashed border-slate-300 bg-white text-[11px] text-fg-subtle dark:border-slate-600 dark:bg-slate-800' : '',
                    isDone ? 'h-6 w-6 border-sky-600 bg-sky-600 text-[11px] text-white' : '',
                    isActive && isReturned ? 'h-6 w-6 scale-125 border-red-500 bg-red-500 text-[11px] text-white ring-4 ring-red-300/60 dark:ring-red-500/30' : '',
                    isActive && !isReturned ? 'h-6 w-6 scale-125 border-amber-500 bg-amber-500 text-[11px] text-amber-950 ring-4 ring-amber-300/60 dark:ring-amber-500/30' : '',
                    !isDone && !isActive && !isDocumentalPulada ? 'h-6 w-6 border-slate-300 bg-white text-[11px] text-fg-subtle dark:border-slate-600 dark:bg-slate-800' : '',
                  ].join(' ')}
                >
                  {isDocumentalPulada ? '–' : isDone ? <CheckIcon /> : isActive && isReturned ? '!' : i + 1}
                </span>
                {/* Label */}
                <span className={[
                  'mt-2 leading-tight',
                  isDone ? 'text-[0.75rem] font-medium text-slate-500 dark:text-slate-500' : '',
                  isActive && isReturned ? 'text-[0.8rem] font-bold text-red-600 dark:text-red-400' : '',
                  isActive && !isReturned ? 'text-[0.8rem] font-bold text-amber-700 dark:text-amber-300' : '',
                  !isDone && !isActive ? 'text-[0.75rem] font-medium text-fg-subtle' : '',
                ].join(' ')}>
                  {step.label}
                </span>
                {/* Owner */}
                <span className={[
                  'mt-0.5 text-[0.62rem] uppercase tracking-[0.06em]',
                  isActive && isReturned ? 'font-semibold text-red-600 dark:text-red-400' : '',
                  isActive && !isReturned ? 'font-semibold text-amber-700 dark:text-amber-400' : '',
                  !isActive ? 'text-fg-subtle' : '',
                ].join(' ')}>
                  {step.owner}
                </span>
                {isDocumentalPulada && (
                  <span className="mt-0.5 text-[0.6rem] font-medium italic text-fg-subtle">
                    não se aplica
                  </span>
                )}
                {isActive && <span className={`mt-1 h-1 w-1 rounded-full ${isReturned ? 'bg-red-400' : 'bg-amber-400'}`} />}
              </li>
            );
          })}
        </ol>
        {isDireta && (
          <p className="mt-4 flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300">
            <span aria-hidden="true">ℹ️</span>
            <span>
              <b>Emenda Direta:</b> a etapa <b>Documental (Convênios)</b> não é exigida — após a aprovação da demanda, a emenda segue direto para a execução.
            </span>
          </p>
        )}
      </div>


      {/* ── Main content ─────────────────────────────────────────────────────── */}
      <div className="mt-6">

        {/* Content column */}
        <div className="flex flex-col gap-5">

          {/* ── Objeto da emenda ────────────────────────────────────────────── */}
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Objeto da emenda</h2>
            <p className="mt-4 text-base leading-relaxed text-slate-700 dark:text-slate-300">
              {emenda.description || <span className="text-fg-subtle">Não informado</span>}
            </p>
            {emenda.objectDetail && (
              <p className="mt-3 text-sm leading-relaxed text-slate-600 dark:text-slate-400">{emenda.objectDetail}</p>
            )}
            {emenda.secretariaDestino && (
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400">
                  Destino: <b className="font-semibold">{emenda.secretariaDestino}</b>
                </span>
              </div>
            )}
          </section>

          {/* ── Dados da emenda ─────────────────────────────────────────────── */}
          <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Dados da emenda</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Informações cadastrais e contexto da proposta.</p>
            <div className="my-5 h-px bg-slate-200 dark:bg-slate-700" />
            <dl className="grid grid-cols-2 gap-x-6 gap-y-5 sm:grid-cols-3">
              {[
                { label: 'Número da emenda', value: emenda.officialCode, mono: true },
                { label: 'Valor', value: fMoney(emenda.value) },
                { label: 'Apresentada em', value: fDate(emenda.date) },
                { label: 'Exercício', value: emenda.exercicio },
                { label: 'Transferência', value: emenda.tipoTransferencia },
                { label: 'Esfera', value: emenda.esfera },
                { label: 'Tipo de emenda', value: emenda.classification ? resolveTipoEmendaNome(emenda.classification, tiposEmenda) : null },
                { label: 'Classificação', value: classificacao },
                { label: 'Função / área', value: funcao },
                { label: 'Parlamentar', value: emenda.councilorName || emenda.councilorId },
              ].map(({ label, value, mono, span2 }) => (
                <div key={label} className={span2 ? 'col-span-2' : ''}>
                  <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{label}</dt>
                  <dd className={`mt-1.5 text-sm font-medium ${mono ? 'font-mono' : ''} ${value ? 'text-slate-900 dark:text-slate-100' : 'font-normal text-fg-subtle'}`}>
                    {value ? String(value) : 'Não informado'}
                  </dd>
                </div>
              ))}
              <div className="col-span-2">
                <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Dotação orçamentária</dt>
                <dd className="mt-1.5 text-sm font-medium text-slate-900 dark:text-slate-100">
                  {(() => {
                    const dotacoes = (emenda.dotacoes && emenda.dotacoes.length > 0)
                      ? emenda.dotacoes.map(d => d.dotacaoOrcamentariaTexto).filter(Boolean) as string[]
                      : (emenda.dotacaoOrcamentariaTexto ? [emenda.dotacaoOrcamentariaTexto] : []);
                    return dotacoes.length > 0
                      ? dotacoes.map((d, i) => <div key={i}>{d}</div>)
                      : <span className="font-normal text-fg-subtle">Não informado</span>;
                  })()}
                </dd>
              </div>
            </dl>
            {emenda.justificativa && (
              <>
                <div className="my-5 h-px bg-slate-200 dark:bg-slate-700" />
                <div>
                  <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Justificativa</dt>
                  <dd className="mt-2 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-300">{emenda.justificativa}</dd>
                </div>
              </>
            )}
          </section>

          {emAprovacaoCamara && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-5 dark:border-amber-800/50 dark:bg-amber-900/20">
              <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Em aprovação na Câmara</p>
              <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                Esta emenda ainda não foi enviada à Prefeitura. Ao aprovar, ela passa ao status Recebido e entra na fila do Orçamento.
              </p>
              {canAprovarCamara && (
                <div className="mt-3">
                  {!emenda?.classification && (
                    <div className="mb-3">
                      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Tipo da emenda *</label>
                      <select value={tipoCamara} onChange={(e) => setTipoCamara(e.target.value)} className={FIELD_CLASS}>
                        <option value="">Selecione…</option>
                        {tiposEmenda.filter((t) => t.ativo).map((t) => <option key={t.codigo} value={t.codigo}>{t.nome}</option>)}
                      </select>
                    </div>
                  )}
                  {!emenda?.esfera && (
                    <div className="mb-3">
                      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Esfera *</label>
                      <select value={esferaCamara} onChange={(e) => setEsferaCamara(e.target.value)} className={FIELD_CLASS}>
                        <option value="">Selecione…</option>
                        <option value="Municipal">Municipal</option>
                        <option value="Estadual">Estadual</option>
                        <option value="Federal">Federal</option>
                      </select>
                    </div>
                  )}
                  {precisaDotacaoCamara && (
                    <div className="mb-3">
                      <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-300">Dotação orçamentária *</label>
                      <DotacaoOrcamentariaCombobox
                        options={dotacoesOpcoes}
                        value={dotacaoCamara.id}
                        valueText={dotacaoCamara.texto}
                        onChange={(dotId, texto) => setDotacaoCamara({ id: dotId, texto })}
                      />
                    </div>
                  )}
                  <Button variant="success" onClick={handleAprovarCamara} disabled={aprovandoCamara}>
                    {aprovandoCamara ? 'Aprovando…' : 'Aprovar e enviar à Prefeitura'}
                  </Button>
                  {aprovarCamaraErro && <p className="mt-2 text-sm text-red-600">{aprovarCamaraErro}</p>}
                </div>
              )}
            </div>
          )}

          {/* ── Instituição & convênio ──────────────────────────────────────── */}
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
            <details className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 [&::-webkit-details-marker]:hidden">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-700/50 dark:text-slate-400">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                      <path d="M3 21h18" /><path d="M5 21V7l8-4v18" /><path d="M19 21V11l-6-4" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Instituição &amp; convênio</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Beneficiária e vínculo de convênio</p>
                  </div>
                </div>
                <ChevronDown className="text-fg-subtle transition group-open:rotate-180" />
              </summary>
              <div className="border-t border-slate-200 px-6 pb-6 pt-4 dark:border-slate-700">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-700/30">
                    <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Instituição</p>
                    <p className="mt-2 text-sm font-medium text-slate-900 dark:text-slate-100">{emenda.institutionName || 'Não informada'}</p>
                    {emenda.institutionEmail && (
                      <p className="mt-0.5 text-sm text-fg-subtle">{emenda.institutionEmail}</p>
                    )}
                  </div>
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-700/30">
                    <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">Convênio</p>
                    <div className="mt-2 flex gap-6">
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Existe</p>
                        <p className="mt-0.5 text-sm font-medium text-slate-900 dark:text-slate-100">{emenda.existeConvenio ? 'Sim' : 'Não'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Número</p>
                        <p className={`mt-0.5 text-sm font-medium ${emenda.numeroConvenio ? 'text-slate-900 dark:text-slate-100' : 'text-fg-subtle'}`}>{emenda.numeroConvenio || '—'}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Ano</p>
                        <p className={`mt-0.5 text-sm font-medium ${emenda.anoConvenio ? 'text-slate-900 dark:text-slate-100' : 'text-fg-subtle'}`}>{emenda.anoConvenio || '—'}</p>
                      </div>
                    </div>
                  </div>
                </div>
                {emenda.signedLink && (
                  <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 dark:border-sky-800 dark:bg-sky-900/20">
                    <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-sky-700 dark:text-sky-400">Documento assinado</p>
                    <a href={emenda.signedLink} target="_blank" rel="noopener noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-sky-700 hover:underline dark:text-sky-400">
                      Abrir documento →
                    </a>
                  </div>
                )}
              </div>
            </details>
          </section>

          {/* ── Vínculo orçamentário (Orçamento, qualquer fase) ────────────── */}
          {id && (
            <EmendaVinculoOrcamentarioPanel
              emendaId={id}
              exercicio={emenda.exercicio}
              leiOrcamentariaId={emenda.leiOrcamentariaId}
              canManageLeis={canManageLeis}
              tipo={emenda.atoNormativoTipo}
              numero={emenda.atoNormativoNumero}
              ano={emenda.atoNormativoAno}
              url={emenda.atoNormativoUrl}
              canEdit={canEditVinculoOrcamentario}
              onSaved={(updated) => {
                setEmenda(prev => (prev ? { ...prev, ...(updated as Partial<Emenda>) } : prev));
                emendaService.getHistorico(id).then(setHistorico).catch(() => {});
              }}
            />
          )}

          {/* ── Ações da tramitação ─────────────────────────────────────────── */}
          {/* ── Workflow action panel (only the current stage's) ────────────── */}
          {id && (activeStep === 0 || activeStep === 1) && (
            <AdmissibilidadePanel
              emendaId={id}
              emendaStatus={statusWorkflow}
              canAct={canActAdmissibilidade}
              onStatusChange={refreshWorkflowState}
            />
          )}
          {id && activeStep === 2 && (
            <SecretariaDemandaPanel
              emendaId={id}
              emendaStatus={statusWorkflow}
              tipoTransferencia={emenda.tipoTransferencia}
              canAct={canActSecretaria}
              onStatusChange={refreshWorkflowState}
            />
          )}
          {id && activeStep === 3 && (
            <ConveniosDocumentalPanel
              emendaId={id}
              emendaStatus={statusWorkflow}
              tipoTransferencia={emenda.tipoTransferencia}
              canAct={(isAdmin || isConvenios) && emenda.tipoTransferencia === 'Indireta'}
              onStatusChange={refreshWorkflowState}
            />
          )}
          {/* Retorno ao Orçamento (demanda/documental): o Orçamento devolve à Câmara. */}
          {id && (
            <OrcamentoDevolucaoPanel
              emendaId={id}
              emendaStatus={statusWorkflow}
              canAct={canActAdmissibilidade}
              onStatusChange={refreshWorkflowState}
            />
          )}
          {/* Execução (4): dirigida pelo plano de trabalho — o card do plano
              vinculado é exibido mais abaixo; não há ação do usuário aqui. */}

          {/* ── Plano de trabalho vinculado ─────────────────────────────────── */}
          {plano && (
            <section className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-700 dark:bg-slate-800/60">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Plano de trabalho</h2>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{plano.titulo || 'Sem título'}</p>
                </div>
                <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${PLANO_STATUS_TONE[plano.status ?? ''] ?? 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-700/50 dark:text-slate-300'}`}>
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  {PLANO_STATUS_LABEL[plano.status ?? ''] ?? plano.status ?? '—'}
                </span>
              </div>
              <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-4 sm:grid-cols-4">
                {[
                  { label: 'Modalidade', value: plano.modalidade === 'TERMO_COLABORACAO' ? 'Termo de colaboração' : plano.modalidade === 'TERMO_FOMENTO' ? 'Termo de fomento' : plano.modalidade },
                  { label: 'Valor', value: plano.valor != null ? fMoney(plano.valor) : null },
                  { label: 'Período', value: (plano.periodoInicio || plano.periodoFim) ? `${plano.periodoInicio ? fDate(plano.periodoInicio) : '—'} a ${plano.periodoFim ? fDate(plano.periodoFim) : '—'}` : null },
                  { label: 'Protocolo', value: plano.numeroProtocolo },
                ].map(({ label, value }) => (
                  <div key={label}>
                    <dt className="text-[0.6875rem] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">{label}</dt>
                    <dd className={`mt-1.5 text-sm font-medium ${value ? 'text-slate-900 dark:text-slate-100' : 'font-normal text-fg-subtle'}`}>
                      {value || 'Não informado'}
                    </dd>
                  </div>
                ))}
              </dl>
              <div className="mt-5 flex flex-wrap gap-2">
                <Button variant="primary" onClick={() => navigate(`/dashboard/plano/full/${plano.id}`)}>
                  Abrir plano de trabalho
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-3.5 w-3.5">
                    <path d="M5 12h14M13 6l6 6-6 6" />
                  </svg>
                </Button>
              </div>
            </section>
          )}

          {/* Execução sem plano vinculado: orienta em vez de deixar a etapa vazia. */}
          {!plano && activeStep === 4 && (
            <section className="rounded-3xl border border-slate-200 bg-slate-100/70 p-6 text-center dark:border-slate-700 dark:bg-slate-900/40">
              <h2 className="text-base font-semibold text-slate-700 dark:text-slate-200">Execução</h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                A execução é conduzida pelo plano de trabalho. Nenhum plano vinculado a esta emenda ainda — vincule um plano na gestão do convênio.
              </p>
            </section>
          )}

          {/* ── Collapsible sections ─────────────────────────────────────────── */}
          <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800/60">

            {/* Anexos */}
            <details className="group">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 [&::-webkit-details-marker]:hidden">
                <div className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-700/50 dark:text-slate-400">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                      <path d="M14 2v6h6" />
                    </svg>
                  </span>
                  <div>
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Anexos</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">Documentos vinculados à emenda</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {generalAnexos.length > 0 && (
                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.7rem] font-semibold text-slate-600 dark:bg-slate-700 dark:text-slate-300">
                      {generalAnexos.length}
                    </span>
                  )}
                  <ChevronDown className="text-fg-subtle transition group-open:rotate-180" />
                </div>
              </summary>
              <div className="border-t border-slate-200 px-6 pb-6 pt-4 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500 dark:text-slate-400">Documentos gerais vinculados à emenda.</p>
                  <input
                    ref={inputRef}
                    type="file"
                    className="hidden"
                    onChange={async (e) => {
                      const f = e.target.files?.[0];
                      if (!f) return;
                      setEvidenciaErro(null);
                      if (publicarEvidencia && (!evidenciaForm.categoriaTaep || !evidenciaForm.descricaoPublica.trim() || !evidenciaForm.dataDocumento || !evidenciaForm.redacaoConfirmada)) {
                        setEvidenciaErro('Preencha a categoria, descrição, data e confirme a redação antes de publicar.');
                        e.target.value = '';
                        return;
                      }
                      try {
                        await uploadFile(f, publicarEvidencia ? { ...evidenciaForm, publicavel: true } : undefined);
                        if (publicarEvidencia) {
                          setEvidenciaForm({ categoriaTaep: '', descricaoPublica: '', dataDocumento: '', redacaoConfirmada: false });
                          setPublicarEvidencia(false);
                        }
                      } catch {
                        setEvidenciaErro('Não foi possível enviar o arquivo. Verifique os dados e tente novamente.');
                      }
                    }}
                  />
                  <Button variant="primary" onClick={() => inputRef.current?.click()} disabled={uploading}>
                    {uploading ? 'Enviando…' : 'Adicionar'}
                  </Button>
                </div>
                {canEdit && (
                  <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/40">
                    <label className="flex items-start gap-2 text-sm font-medium text-slate-800 dark:text-slate-100">
                      <input type="checkbox" checked={publicarEvidencia} onChange={e => { setPublicarEvidencia(e.target.checked); setEvidenciaErro(null); }} className="mt-0.5" />
                      Publicar este arquivo como evidência de execução no Portal da Transparência
                    </label>
                    {publicarEvidencia && (
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Categoria TAEP
                          <select value={evidenciaForm.categoriaTaep} onChange={e => setEvidenciaForm(f => ({ ...f, categoriaTaep: e.target.value }))} className={`${FIELD_CLASS} mt-1`}>
                            <option value="">Selecione</option>
                            <option value="NOTA_FISCAL">Nota fiscal</option>
                            <option value="MEDICAO_ATESTO">Medição ou atesto</option>
                            <option value="RECIBO">Recibo</option>
                            <option value="RELATORIO_EXECUCAO">Relatório de execução</option>
                            <option value="FOTO">Foto</option>
                            <option value="COMPROVANTE">Comprovante</option>
                          </select>
                        </label>
                        <label className="text-xs font-semibold text-slate-600 dark:text-slate-300">Data do documento
                          <input type="date" value={evidenciaForm.dataDocumento} onChange={e => setEvidenciaForm(f => ({ ...f, dataDocumento: e.target.value }))} className={`${FIELD_CLASS} mt-1`} />
                        </label>
                        <label className="text-xs font-semibold text-slate-600 md:col-span-2 dark:text-slate-300">Descrição pública
                          <input type="text" maxLength={500} value={evidenciaForm.descricaoPublica} onChange={e => setEvidenciaForm(f => ({ ...f, descricaoPublica: toUpperText(e.target.value) }))} placeholder="Descreva o que o documento comprova" className={`${FIELD_CLASS} mt-1`} />
                        </label>
                        <label className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-950 md:col-span-2 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-100">
                          <input type="checkbox" checked={evidenciaForm.redacaoConfirmada} onChange={e => setEvidenciaForm(f => ({ ...f, redacaoConfirmada: e.target.checked }))} className="mt-0.5" />
                          Confirmo que revisei o arquivo e removi CPF, assinatura, telefone, endereço pessoal e dados de terceiros não exigidos para transparência.
                        </label>
                      </div>
                    )}
                    {evidenciaErro && <p className="mt-3 text-sm font-medium text-red-600">{evidenciaErro}</p>}
                  </div>
                )}
                <div className="mt-4">
                  {generalAnexos.length === 0 ? (
                    <p className="text-sm text-fg-subtle">Nenhum anexo adicionado.</p>
                  ) : (
                    <div className="space-y-2">
                      {generalAnexos.map((a) => <AnexoItem key={a.id} anexo={a} />)}
                    </div>
                  )}
                </div>
              </div>
            </details>

            {/* Recebido da Câmara — oculto no front por enquanto (lógica mantida).
                Reativar trocando RECEBIDO_CAMARA_ENABLED para true. */}
            {RECEBIDO_CAMARA_ENABLED && (isAdmin || isOrcamento) && activeStep <= 1 && (
              <details className="group border-t border-slate-200 dark:border-slate-700">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-4 hover:bg-slate-50 dark:hover:bg-slate-700/30 [&::-webkit-details-marker]:hidden">
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-600 dark:border-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                        <path d="M12 22C6.5 22 2 17.5 2 12S6.5 2 12 2s10 4.5 10 10" />
                        <polyline points="12 6 12 12 16 14" />
                      </svg>
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">Recebido da Câmara</p>
                      <p className="text-xs text-slate-500 dark:text-slate-400">Registrar dados de recebimento pelo legislativo</p>
                    </div>
                  </div>
                  <ChevronDown className="text-fg-subtle transition group-open:rotate-180" />
                </summary>
                <div className="border-t border-slate-200 px-6 pb-6 pt-4 dark:border-slate-700">
                  {!showRecebidoCamaraForm ? (
                    <Button variant="primary" onClick={() => setShowRecebidoCamaraForm(true)}>
                      Registrar recebimento
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-blue-800 dark:text-blue-300">Número da Lei <span className="text-red-500">*</span></label>
                        <input type="text" value={recebidoForm.leiNumero} onChange={(e) => setRecebidoForm((f) => ({ ...f, leiNumero: toUpperText(e.target.value) }))} placeholder="Ex: 3597/2025" className={FIELD_CLASS} />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-blue-800 dark:text-blue-300">Descrição da Lei <span className="text-red-500">*</span></label>
                        <textarea value={recebidoForm.leiDescricao} onChange={(e) => setRecebidoForm((f) => ({ ...f, leiDescricao: toUpperText(e.target.value) }))} rows={3} placeholder="Descrição completa da lei..." className={FIELD_CLASS} />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-semibold text-blue-800 dark:text-blue-300">Data de Recebimento <span className="text-red-500">*</span></label>
                        <input type="date" value={recebidoForm.dataRecebimento} onChange={(e) => setRecebidoForm((f) => ({ ...f, dataRecebimento: e.target.value }))} className={FIELD_CLASS} />
                      </div>
                      {recebidoErro && <p className="text-sm text-danger-text">{recebidoErro}</p>}
                      <div className="flex justify-end gap-2">
                        <button type="button" onClick={() => { setShowRecebidoCamaraForm(false); setRecebidoErro(null); }} className="px-4 py-2 text-sm text-slate-600 hover:text-fg dark:text-slate-400">Cancelar</button>
                        <Button variant="primary" onClick={handleRecebidoCamara} disabled={savingRecebido}>{savingRecebido ? 'Salvando…' : 'Salvar'}</Button>
                      </div>
                    </div>
                  )}
                </div>
              </details>
            )}

          </section>
        </div>

      </div>

      {/* ── Modals ───────────────────────────────────────────────────────────── */}

      {/* Histórico — drawer lateral */}
      {showHistoricoModal && (
        <div className="fixed inset-0 z-50 flex justify-end overflow-hidden bg-black/40" onClick={() => setShowHistoricoModal(false)}>
          <div
            ref={historicoDrawerRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="historico-drawer-title"
            tabIndex={-1}
            className="flex h-full w-full max-w-md flex-col bg-slate-50 shadow-xl outline-none dark:bg-slate-900"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-slate-200 px-5 py-4 dark:border-slate-700">
              <h2 id="historico-drawer-title" className="text-base font-semibold text-slate-900 dark:text-slate-100">Histórico de tramitação</h2>
              <button
                type="button"
                onClick={() => setShowHistoricoModal(false)}
                className="rounded-lg p-1.5 text-fg-muted transition hover:bg-slate-200 hover:text-fg dark:hover:bg-slate-700 dark:hover:text-slate-200"
                aria-label="Fechar histórico"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5">
              <EmendaHistoricoTimeline items={itemsHistorico} loading={loadingHistorico} />
            </div>
          </div>
        </div>
      )}

      {showAlterarStatusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl dark:bg-slate-800">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">Alterar status da emenda</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Selecione o novo status. Esta ação será registrada no histórico e na auditoria.</p>
            <div className="mt-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Novo status</label>
                <select value={novoStatusSelecionado} onChange={(e) => setNovoStatusSelecionado(e.target.value)} className={`${FIELD_CLASS} mt-1`}>
                  <option value="">Selecione…</option>
                  {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Justificativa <span className="text-fg-subtle">(opcional)</span></label>
                <textarea value={justificativaStatus} onChange={(e) => setJustificativaStatus(toUpperText(e.target.value))} rows={3} className={`${FIELD_CLASS} mt-1`} placeholder="Descreva o motivo da alteração…" />
              </div>
              {alterarStatusErro && <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-900/20 dark:text-red-400">{alterarStatusErro}</p>}
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <Button variant="secondary" onClick={() => setShowAlterarStatusModal(false)} disabled={alterandoStatus}>Cancelar</Button>
              <Button variant="warning" onClick={handleAlterarStatus} disabled={alterandoStatus || !novoStatusSelecionado}>
                {alterandoStatus ? 'Salvando…' : 'Confirmar'}
              </Button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default EmendaDetailPage;
